import type { SlotTargeting } from "@/ts/types";

export const formatBidderTTRBucket = (timeToRespond: number): string => {
	const roundedTime = Math.max(0, Math.ceil(timeToRespond));

	if (roundedTime <= 1500) {
		return String(Math.max(100, Math.ceil(roundedTime / 100) * 100)).padStart(
			4,
			"0",
		);
	}

	if (roundedTime <= 2000) {
		return String(Math.ceil(roundedTime / 250) * 250).padStart(4, "0");
	}

	if (roundedTime <= 3000) {
		return String(Math.ceil(roundedTime / 500) * 500).padStart(4, "0");
	}

	return "3001";
};

type PrebidEventEntry = {
	eventType: string;
	args: Record<string, unknown>;
	elapsedTime: number;
};

/**
 * Build TTR targeting from Prebid's getEvents() output.
 * Called after auction completes — getEvents() returns all events that
 * already fired, so we filter to the relevant auction by auctionId.
 *
 * Uses:
 * - auctionInit: discover all configured bidders per slot, set default to timeout
 * - bidResponse: overwrite with actual timeToRespond
 * - noBid: overwrite with elapsedTime for bidders that explicitly returned no bid
 */
export const buildTTRTargeting = (
	events: PrebidEventEntry[],
	auctionId: string,
	timeout: number,
): Record<string, SlotTargeting> => {
	const ttrMap: Record<string, Record<string, number>> = {};

	for (const event of events) {
		if (event.args.auctionId !== auctionId) continue;

		switch (event.eventType) {
			case "auctionInit": {
				const adUnits = event.args.adUnits as
					| { code: string; bids: { bidder: string }[] }[]
					| undefined;
				if (!adUnits) break;

				for (const adUnit of adUnits) {
					ttrMap[adUnit.code] ??= {};
					for (const bid of adUnit.bids) {
						ttrMap[adUnit.code][bid.bidder] = timeout;
					}
				}
				break;
			}
			case "bidResponse": {
				const adUnitCode = event.args.adUnitCode as string | undefined;
				const bidderCode = event.args.bidderCode as string | undefined;
				const timeToRespond = event.args.timeToRespond as number | undefined;
				if (!adUnitCode || !bidderCode) break;

				ttrMap[adUnitCode] ??= {};
				ttrMap[adUnitCode][bidderCode] =
					typeof timeToRespond === "number" ? timeToRespond : event.elapsedTime;
				break;
			}
			case "noBid": {
				const adUnitCode = event.args.adUnitCode as string | undefined;
				const bidder = event.args.bidder as string | undefined;
				if (!adUnitCode || !bidder) break;

				ttrMap[adUnitCode] ??= {};
				ttrMap[adUnitCode][bidder] = event.elapsedTime;
				break;
			}
		}
	}

	const targeting: Record<string, SlotTargeting> = {};

	for (const [slotId, bidders] of Object.entries(ttrMap)) {
		targeting[slotId] = {};
		for (const [bidder, time] of Object.entries(bidders)) {
			targeting[slotId][`hb_${bidder}_ttr`] = formatBidderTTRBucket(time);
		}
	}

	return targeting;
};
